import * as React from 'react';
import {
  AppBar,
  Box,
  Alert,
  Button,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LoginIcon from '@mui/icons-material/Login';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
// TODO: Re-enable when scheduling/timetable feature is ready
// import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useStudentSession } from '../hooks/useStudentSession.ts';
import { useTutorSession } from '../context/TutorSessionContext.tsx';
import { needsSignupCompletion } from '../utils/routeAfterSignIn.ts';
import { supabase } from '../supabaseClient.ts';

const mobileDrawerWidth = 260;
const toolbarMinHeight = { xs: 64, md: 80 };
const ADMIN_APP_FALLBACK_URL =
  (process.env.REACT_APP_ADMIN_APP_URL ?? 'http://localhost:5173').replace(/\/+$/, '');

type NavItem = { label: string; to: string; icon: React.ReactNode; requiresAuth?: boolean };

const NAV_ITEMS: NavItem[] = [
  { label: 'Lessons', to: '/lessons', icon: <MenuBookOutlinedIcon />, requiresAuth: true },
  { label: 'Tests', to: '/tests', icon: <AssignmentOutlinedIcon />, requiresAuth: true },
  { label: 'Practice', to: '/practice', icon: <SchoolOutlinedIcon /> },
  // TODO: Re-enable when scheduling/timetable feature is ready
  // { label: 'Timetable', to: '/timetable', icon: <CalendarMonthIcon /> },
];

const ACCOUNT_ITEM: NavItem = {
  label: 'Profile',
  to: '/profile',
  icon: <PersonOutlineIcon />,
};

// Signed-out visitors land here from the public practice page, so they most
// likely don't have an account yet. The signup page links onward to login.
const SIGNUP_ITEM: NavItem = {
  label: 'Sign up',
  to: '/signup',
  icon: <LoginIcon />,
};

const Brand: React.FC<{ compact?: boolean }> = ({ compact }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    {/* Halo behind the logo, so it reads against the white bar without an outline */}
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        p: 0.75,
        borderRadius: '50%',
      }}
    >
      <Box
        component="img"
        src="/wizardhat2.svg"
        alt="Wizard Logo"
        sx={{
          height: compact ? 20 : 24,
          width: 'auto',
          display: 'block',
        }}
      />
    </Box>
    <Typography
      variant="button"
      noWrap
      sx={{ fontSize: '1.4rem', fontWeight: 500, letterSpacing: '0.02em', lineHeight: 1.5 }}
    >
      StudyCharm
    </Typography>
  </Box>
);

type NavProps = {
  onNavigate: (path: string) => void;
  activePath: string;
  navItems: NavItem[];
  accountItem?: NavItem;
  locked: boolean;
};

const disabledNavSx = {
  '&.Mui-disabled': { color: 'inherit', opacity: 0.35 },
};

// Button icons are a fixed 20px by default, so they need scaling alongside the label.
const navTextSx = {
  fontSize: '1.1rem',
  '& .MuiButton-startIcon > *:first-of-type': { fontSize: '1.1rem' },
};

const NavButton: React.FC<{
  item: NavItem;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ item, selected, disabled, onClick }) => (
  <Button
    onClick={onClick}
    disabled={disabled}
    disableRipple
    startIcon={item.icon}
    sx={{
      px: 1,
      py: 0.5,
      minWidth: 0,
      color: selected ? 'text.primary' : 'text.secondary',
      fontWeight: selected ? 400 : 300,
      bgcolor: 'transparent',
      '&:hover': { bgcolor: 'transparent', color: 'text.primary' },
      ...navTextSx,
      ...disabledNavSx,
    }}
  >
    {item.label}
  </Button>
);

const AccountButton: React.FC<{
  item: NavItem;
  selected: boolean;
  onClick: () => void;
}> = ({ item, selected, onClick }) => (
  <Button
    variant="contained"
    disableElevation
    onClick={onClick}
    sx={{
      px: 2.5,
      py: 0.75,
      borderRadius: 2,
      fontWeight: 500,
      color: 'common.white',
      bgcolor: selected ? 'primary.dark' : 'primary.main',
      ...navTextSx,
    }}
  >
    {item.label}
  </Button>
);

const TopNav: React.FC<NavProps> = ({ onNavigate, activePath, navItems, locked }) => (
  <Box component="nav" sx={{ display: 'flex', alignItems: 'center', gap: 5 }}>
    {navItems.map((item) => (
      <NavButton
        key={item.to}
        item={item}
        selected={activePath.startsWith(item.to)}
        disabled={locked && !!item.requiresAuth}
        onClick={() => onNavigate(item.to)}
      />
    ))}
  </Box>
);

const navListItemSx = {
  mx: 1,
  my: 0.5,
  borderRadius: 1.5,
  px: 2,
  py: 1.25,
  '& .MuiListItemIcon-root': { minWidth: 36, mr: 1.5, color: 'inherit' },
  '&.Mui-selected': { bgcolor: 'action.selected' },
  '&.Mui-selected:hover': { bgcolor: 'action.hover' },
  ...disabledNavSx,
};

const MobileNav: React.FC<NavProps> = ({
  onNavigate,
  activePath,
  navItems,
  accountItem,
  locked,
}) => (
  <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
    <Toolbar sx={{ px: 2, minHeight: toolbarMinHeight }}>
      <Brand compact />
    </Toolbar>

    <Divider />

    <List sx={{ width: '100%', py: 1 }}>
      {navItems.map((item) => (
        <ListItemButton
          key={item.to}
          selected={activePath.startsWith(item.to)}
          disabled={locked && !!item.requiresAuth}
          onClick={() => onNavigate(item.to)}
          sx={navListItemSx}
        >
          <ListItemIcon>{item.icon}</ListItemIcon>
          <ListItemText primary={item.label} />
        </ListItemButton>
      ))}
    </List>

    {accountItem && (
      <Box sx={{ mt: 'auto' }}>
        <Divider />
        <List sx={{ width: '100%', py: 1 }}>
          <ListItemButton
            selected={activePath.startsWith(accountItem.to)}
            onClick={() => onNavigate(accountItem.to)}
            sx={navListItemSx}
          >
            <ListItemIcon>{accountItem.icon}</ListItemIcon>
            <ListItemText primary={accountItem.label} />
          </ListItemButton>
        </List>
      </Box>
    )}
  </Box>
);

const DashboardLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const { isTutorSession, tutorSession, endTutorSession } = useTutorSession();
  // Track student activity sessions for normal student browsing only.
  useStudentSession(!isTutorSession);

  // Starts as null so the nav isn't dimmed while the session check is in flight.
  const [isAuthed, setIsAuthed] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setIsAuthed(!!session));
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_e, session) => setIsAuthed(!!session));
    return () => subscription.unsubscribe();
  }, []);

  // Public self-signup students have no company, and lessons are company content.
  const [isPublicStudent, setIsPublicStudent] = React.useState(false);

  React.useEffect(() => {
    if (isTutorSession || isAuthed !== true) {
      setIsPublicStudent(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from('students')
        .select('company_id')
        .eq('user_id', user.id)
        .maybeSingle();
      if (!cancelled) setIsPublicStudent(!!data && !data.company_id);
    })();
    return () => { cancelled = true; };
  }, [isAuthed, isTutorSession]);

  // Google (or unfinished) signups have a session but no student row yet.
  // null = still checking, so we don't flash Profile and let them click it.
  const [needsComplete, setNeedsComplete] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    if (isTutorSession || isAuthed !== true) {
      setNeedsComplete(false);
      return;
    }
    let cancelled = false;
    setNeedsComplete(null);
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;
      const needed = await needsSignupCompletion(user);
      if (!cancelled) setNeedsComplete(needed);
    })();
    return () => { cancelled = true; };
  }, [isAuthed, isTutorSession]);

  const navItems = React.useMemo(
    () => (isPublicStudent ? NAV_ITEMS.filter((i) => i.to !== '/lessons') : NAV_ITEMS),
    [isPublicStudent],
  );

  const locked = isAuthed === false && !isTutorSession;

  // Hide the slot until we know the session. While loading, isAuthed is null
  // so a previous version flashed "Profile" and sent signed-out visitors to
  // /profile, which then bounced them toward signup.
  const accountItem = isTutorSession
    ? undefined
    : isAuthed === false
      ? SIGNUP_ITEM
      : isAuthed === true && needsComplete === false
        ? ACCOUNT_ITEM
        : undefined;

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const handleDrawerToggle = () => setMobileOpen((p) => !p);

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  if (needsComplete) {
    return <Navigate to="/signup/complete" replace />;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <CssBaseline />

      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: 'background.default',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar
          sx={{ minHeight: toolbarMinHeight, gap: { xs: 1, md: 6 }, px: { xs: 2, md: 8 } }}
        >
          {!isMdUp && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="open navigation"
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Brand />

          {isMdUp && (
            <TopNav
              onNavigate={handleNavigate}
              activePath={location.pathname}
              navItems={navItems}
              locked={locked}
            />
          )}

          <Box sx={{ flexGrow: 1 }} />

          {isMdUp && accountItem && (
            <AccountButton
              item={accountItem}
              selected={location.pathname.startsWith(accountItem.to)}
              onClick={() => handleNavigate(accountItem.to)}
            />
          )}
        </Toolbar>
      </AppBar>

      {/* Nav collapses into a drawer below the md breakpoint */}
      <Drawer
        variant="temporary"
        open={!isMdUp && mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: mobileDrawerWidth,
            boxSizing: 'border-box',
            bgcolor: 'background.default',
            color: 'text.primary',
          },
        }}
      >
        <MobileNav
          onNavigate={handleNavigate}
          activePath={location.pathname}
          navItems={navItems}
          accountItem={accountItem}
          locked={locked}
        />
      </Drawer>

      {/* Spacer that clears the fixed app bar */}
      <Toolbar sx={{ minHeight: toolbarMinHeight }} />

      <Box component="main" sx={{ p: { xs: 2, sm: 3 }, position: 'relative' }}>
        {isTutorSession && (
          <Alert
            severity="info"
            sx={{ mb: 2, display: 'flex', alignItems: 'center' }}
            action={
              <Button
                size="small"
                color="inherit"
                onClick={async () => {
                  try {
                    await endTutorSession();
                    const redirectUrl = tutorSession?.returnUrl || ADMIN_APP_FALLBACK_URL;
                    // If the tutor session was launched in a new tab, close it.
                    // Browsers may block close in some cases, so always fall back to redirect.
                    try {
                      window.close();
                    } catch {
                      // ignore and rely on fallback redirect
                    }
                    window.setTimeout(() => {
                      if (!window.closed) {
                        window.location.href = redirectUrl;
                      }
                    }, 200);
                  } catch {
                    // Keep session if end failed.
                  }
                }}
                aria-label="end tutor session"
              >
                End session
              </Button>
            }
          >
            Tutor session is active. Private student sections are hidden.
          </Alert>
        )}

        <Outlet />
      </Box>
    </Box>
  );
};

export default DashboardLayout;
